import {
  applyMaskToPixels,
  invertMaskPixels,
  type MaskMode,
  rectMaskPixels,
  solidMaskPixels,
} from '../core/model/mask';
import type { Rect, Size } from '../core/model/types';
import { decodeLayer, encodeLayer } from './raster';

/**
 * Making and editing mask rasters.
 *
 * The maths is all in `core/model/mask.ts`; this file only moves pixels across the
 * canvas boundary. Everything here returns a PNG data URL, because PNG is the one
 * format in play that keeps an alpha channel — and for a mask the alpha channel
 * *is* the content.
 */

/** A mask that reveals everything, or hides everything. */
export function createMaskUrl(size: Size, mode: MaskMode): string {
  return encodeLayer(solidMaskPixels(size, mode));
}

/** A mask that reveals `rect` (in the layer's pixel space) and hides the rest. */
export function maskFromRectUrl(rect: Rect, size: Size): string {
  return encodeLayer(rectMaskPixels(rect, size));
}

/**
 * Bake a mask into a layer's pixels and return the new layer raster.
 *
 * This is the destructive one — "Apply Mask" — which is why it is a command the
 * user asks for rather than something that happens on export.
 */
export async function bakeMaskIntoLayerUrl(
  layerSrc: string,
  size: Size,
  maskSrc: string,
  inverted: boolean,
): Promise<string> {
  const [layer, mask] = await Promise.all([
    decodeLayer(layerSrc, size.width, size.height),
    decodeLayer(maskSrc, size.width, size.height),
  ]);
  return encodeLayer(applyMaskToPixels(layer, mask, { inverted }));
}

/**
 * The alpha coverage a mask contributes, as a canvas: the mask itself, or its
 * opposite when `inverted`.
 *
 * This is the one definition of "what a mask covers" for every surface that
 * composites one — the engine's live bake and the adjustment preview both draw
 * through it, so the preview cannot drift from the scene it imitates. The
 * mask's alpha is the content; inversion is the alpha complement (colour
 * channels are zeroed — they were never the point).
 */
export function maskCoverage(maskImage: HTMLImageElement, inverted: boolean): HTMLCanvasElement {
  const width = Math.max(1, maskImage.naturalWidth);
  const height = Math.max(1, maskImage.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.drawImage(maskImage, 0, 0, width, height);
  if (!inverted) return canvas;
  const pixels = ctx.getImageData(0, 0, width, height);
  pixels.data.set(invertMaskPixels({ data: pixels.data, width, height }).data);
  ctx.clearRect(0, 0, width, height);
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}

/** Load a mask's source and return its coverage canvas. */
export async function loadMaskCoverage(
  maskSrc: string,
  inverted: boolean,
): Promise<HTMLCanvasElement> {
  const image = await loadImage(maskSrc);
  return maskCoverage(image, inverted);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Mask could not be decoded.'));
    image.src = src;
  });
}
